import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Trash2, Edit3, ChevronDown, ChevronUp, AlertTriangle, Clock, User, X, Loader2, RefreshCw, Eye, Shield, Building2, ShoppingCart, Crosshair } from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'all', label: 'All', icon: Shield, color: 'gray' },
  { key: 'staffing', label: 'Staffing', icon: Building2, color: 'orange' },
  { key: 'healthcare_staffing', label: 'Healthcare Staffing', icon: Plus, color: 'red' },
  { key: 'retail', label: 'Retail', icon: ShoppingCart, color: 'blue' },
  { key: 'defense', label: 'Defense', icon: Crosshair, color: 'slate' },
  { key: 'enterprise', label: 'Enterprise', icon: Building2, color: 'purple' },
  { key: 'custom', label: 'Custom', icon: Shield, color: 'emerald' },
];

const CAT_COLORS = {
  staffing: 'bg-orange-50 text-orange-700 border-orange-200',
  healthcare_staffing: 'bg-red-50 text-red-700 border-red-200',
  retail: 'bg-blue-50 text-blue-700 border-blue-200',
  defense: 'bg-slate-100 text-slate-700 border-slate-300',
  enterprise: 'bg-purple-50 text-purple-700 border-purple-200',
  custom: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

/* ── Impact Preview Modal ── */
const ImpactPreview = ({ data, onConfirm, onCancel, saving }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="impact-preview-modal">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-4">
      <h3 className="text-sm font-bold text-[#0A1F44]">Impact Preview</h3>
      <div className="space-y-2">
        <p className="text-xs text-gray-600">
          Adding <strong>{data.company_name}</strong> to the <strong>{data.category}</strong> exclusion list:
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-[10px] text-gray-400">Companies matched</p>
            <p className="text-lg font-bold text-[#0A1F44]">{data.total_matches}</p>
          </div>
          <div className="p-3 bg-red-50 rounded-lg">
            <p className="text-[10px] text-red-400">Would lose qualified</p>
            <p className="text-lg font-bold text-red-700">{data.would_lose_qualified}</p>
          </div>
        </div>
        {data.affected_companies?.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50">
                <th className="px-3 py-2 text-left text-[10px] text-gray-500">Company</th>
                <th className="px-3 py-2 text-left text-[10px] text-gray-500">Current</th>
                <th className="px-3 py-2 text-left text-[10px] text-gray-500">After</th>
              </tr></thead>
              <tbody>
                {data.affected_companies.map((a, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-medium text-[#0A1F44]">{a.company}</td>
                    <td className="px-3 py-2"><span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-[10px]">{a.current_tier}</span></td>
                    <td className="px-3 py-2"><span className="px-1.5 py-0.5 bg-red-50 text-red-700 rounded text-[10px]">{a.would_become}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.override_conflicts?.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-medium text-amber-800">Manual override conflict</p>
              <p className="text-[10px] text-amber-700">{data.override_conflicts.join(', ')} have manual overrides that may be affected.</p>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
        <button onClick={onConfirm} disabled={saving}
          className="px-4 py-2 bg-[#0A1F44] text-white text-xs font-bold rounded-lg hover:bg-[#0d2a5c] disabled:opacity-50 flex items-center gap-1"
          data-testid="confirm-add-btn">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          Confirm & Add
        </button>
      </div>
    </div>
  </div>
);

/* ── Add/Edit Modal ── */
const EntryModal = ({ entry, onSave, onClose }) => {
  const [name, setName] = useState(entry?.company_name || '');
  const [aliases, setAliases] = useState((entry?.aliases || []).join(', '));
  const [category, setCategory] = useState(entry?.category || 'staffing');
  const [notes, setNotes] = useState(entry?.notes || '');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const isEdit = !!entry;

  const handlePreview = async () => {
    if (!name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      const aliasList = aliases.split(',').map(a => a.trim()).filter(Boolean);
      const data = await fetchWithAuth(`${API_URL}/api/settings/exclusion-lists/preview-impact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_name: name.trim(), aliases: aliasList, category }),
      });
      setPreview(data);
      setShowPreview(true);
    } catch { toast.error('Preview failed'); }
    setSaving(false);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      const aliasList = aliases.split(',').map(a => a.trim()).filter(Boolean);
      const payload = { company_name: name.trim(), aliases: aliasList, category, notes: notes.trim() };

      if (isEdit) {
        await fetchWithAuth(`${API_URL}/api/settings/exclusion-lists/${encodeURIComponent(entry.company_name_lower)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        await fetchWithAuth(`${API_URL}/api/settings/exclusion-lists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      toast.success(isEdit ? 'Entry updated' : 'Entry added');
      onSave();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="entry-modal">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-[#0A1F44]">{isEdit ? 'Edit Entry' : 'Add to Exclusion List'}</h3>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4 text-gray-400" /></button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium block mb-1">Company Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g., PRN Healthcare"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none"
                data-testid="entry-name-input" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium block mb-1">Aliases (comma-separated)</label>
              <input type="text" value={aliases} onChange={e => setAliases(e.target.value)}
                placeholder="e.g., PRN Health Services, PRN"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none"
                data-testid="entry-aliases-input" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium block mb-1">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none"
                data-testid="entry-category-select">
                {CATEGORIES.filter(c => c.key !== 'all').map(c => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-500 uppercase tracking-wider font-medium block mb-1">Notes / Reason</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Why is this company excluded?"
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none resize-y"
                data-testid="entry-notes-input" />
            </div>
          </div>

          <div className="flex justify-between pt-2">
            {!isEdit && (
              <button onClick={handlePreview} disabled={saving || !name.trim()}
                className="flex items-center gap-1 px-3 py-2 text-xs text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 disabled:opacity-50"
                data-testid="preview-impact-btn">
                <Eye className="h-3 w-3" /> Preview Impact
              </button>
            )}
            <div className="flex gap-2 ml-auto">
              <button onClick={onClose} className="px-4 py-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
              <button onClick={handleSave} disabled={saving || !name.trim()}
                className="px-4 py-2 bg-[#D4AF37] text-[#0A1F44] text-xs font-bold rounded-lg hover:bg-[#c5a030] disabled:opacity-50 flex items-center gap-1"
                data-testid="save-entry-btn">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                {isEdit ? 'Update' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showPreview && preview && (
        <ImpactPreview data={preview} saving={saving}
          onConfirm={() => { setShowPreview(false); handleSave(); }}
          onCancel={() => setShowPreview(false)} />
      )}
    </>
  );
};

/* ── Audit Log Panel ── */
const AuditLog = ({ onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWithAuth(`${API_URL}/api/settings/exclusion-lists/audit-log?limit=50`)
      .then(d => setLogs(d.entries || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="audit-log-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
          <h3 className="text-sm font-bold text-[#0A1F44]">Audit Log</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : logs.length === 0 ? (
            <p className="text-center text-xs text-gray-400 py-8">No audit entries yet</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="h-6 w-6 bg-[#0A1F44] rounded-full flex items-center justify-center shrink-0">
                    <Clock className="h-3 w-3 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-[#0A1F44]">{log.company_name}</span>
                      <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded border ${CAT_COLORS[log.category] || 'bg-gray-50 text-gray-600'}`}>{log.category}</span>
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[9px] rounded">{log.action}</span>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-0.5">{log.details}</p>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-400">
                      <User className="h-2.5 w-2.5" />{log.by}
                      <Clock className="h-2.5 w-2.5 ml-1" />{new Date(log.at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Main Exclusion List Manager ── */
export default function ExclusionListManager({ embedded = false }) {
  const [entries, setEntries] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(null);

  const fetchEntries = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activeCategory !== 'all') params.set('category', activeCategory);
    if (searchQuery) params.set('search', searchQuery);
    fetchWithAuth(`${API_URL}/api/settings/exclusion-lists?${params}`)
      .then(d => {
        setEntries(d.entries || []);
        setCategoryCounts(d.category_counts || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeCategory, searchQuery]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleDelete = async (entry) => {
    if (!window.confirm(`Remove "${entry.company_name}" from the exclusion list?`)) return;
    try {
      await fetchWithAuth(`${API_URL}/api/settings/exclusion-lists/${encodeURIComponent(entry.company_name_lower)}`, { method: 'DELETE' });
      toast.success('Entry removed');
      fetchEntries();
    } catch { toast.error('Delete failed'); }
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const report = await fetchWithAuth(`${API_URL}/api/settings/exclusion-lists/backfill`, {
        method: 'POST',
      });
      const changed = report.changed_to_excluded || 0;
      toast.success(`Backfill complete: ${changed} companies reclassified`);
    } catch { toast.error('Backfill failed'); }
    setBackfilling(false);
  };

  const totalCustom = Object.values(categoryCounts).reduce((s, c) => s + c, 0);

  return (
    <IntelligenceLayout title="Exclusion List Manager" embedded={embedded}>
    <div className="space-y-6 p-6" data-testid="exclusion-list-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-[#0A1F44]">Exclusion List Manager</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Your custom exclusion rules — {totalCustom} {totalCustom === 1 ? 'entry' : 'entries'}.
            {' '}These are applied <em>in addition to</em> the built-in classifier rules (staffing patterns, retail, defense, enterprise).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAuditLog(true)}
            className="flex items-center gap-1 px-3 py-2 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            data-testid="audit-log-btn">
            <Clock className="h-3 w-3" /> Audit Log
          </button>
          <button onClick={handleBackfill} disabled={backfilling}
            className="flex items-center gap-1 px-3 py-2 text-xs text-[#0A1F44] border border-[#D4AF37] rounded-lg hover:bg-[#D4AF37]/10 disabled:opacity-50"
            data-testid="backfill-btn">
            {backfilling ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Re-run Classification
          </button>
          <button onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1 px-4 py-2 bg-[#0A1F44] text-white text-xs font-bold rounded-lg hover:bg-[#0d2a5c]"
            data-testid="add-entry-btn">
            <Plus className="h-3 w-3" /> Add Company
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-testid="category-tabs">
        {CATEGORIES.map(cat => {
          const count = cat.key === 'all' ? totalCustom : (categoryCounts[cat.key] || 0);
          return (
            <button key={cat.key} onClick={() => setActiveCategory(cat.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold rounded-md transition-all ${
                activeCategory === cat.key
                  ? 'bg-white text-[#0A1F44] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${cat.key}`}>
              <cat.icon className="h-3 w-3" />
              {cat.label}
              {count > 0 && <span className="px-1 py-0 bg-gray-200 text-gray-600 text-[9px] rounded-full">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by company name or alias..."
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50 focus:outline-none"
          data-testid="search-input" />
      </div>

      {/* Entries Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Shield className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No custom exclusion entries{activeCategory !== 'all' ? ` in ${activeCategory}` : ''}</p>
          <p className="text-xs mt-1">Add companies here to exclude them beyond the built-in classifier rules</p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full" data-testid="entries-table">
            <thead>
              <tr className="bg-gray-50 text-[10px] text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2.5 text-left">Company</th>
                <th className="px-4 py-2.5 text-left">Category</th>
                <th className="px-4 py-2.5 text-left">Aliases</th>
                <th className="px-4 py-2.5 text-left">Added By</th>
                <th className="px-4 py-2.5 text-left">Date</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, i) => (
                <React.Fragment key={i}>
                  <tr className={`border-t border-gray-100 hover:bg-gray-50/50 transition-colors ${expandedEntry === i ? 'bg-gray-50' : ''}`}>
                    <td className="px-4 py-2.5">
                      <button onClick={() => setExpandedEntry(expandedEntry === i ? null : i)}
                        className="flex items-center gap-1.5 text-left">
                        {expandedEntry === i ? <ChevronUp className="h-3 w-3 text-gray-400" /> : <ChevronDown className="h-3 w-3 text-gray-400" />}
                        <span className="text-sm font-medium text-[#0A1F44]">{entry.company_name}</span>
                      </button>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded border ${CAT_COLORS[entry.category] || 'bg-gray-50 text-gray-600'}`}>
                        {entry.category?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">
                      {entry.aliases?.length > 0 ? entry.aliases.join(', ') : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{entry.created_by || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-400">{entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditEntry(entry)}
                          className="p-1.5 hover:bg-gray-200 rounded" title="Edit"
                          data-testid={`edit-${i}`}>
                          <Edit3 className="h-3 w-3 text-gray-400" />
                        </button>
                        <button onClick={() => handleDelete(entry)}
                          className="p-1.5 hover:bg-red-50 rounded" title="Remove"
                          data-testid={`delete-${i}`}>
                          <Trash2 className="h-3 w-3 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedEntry === i && (
                    <tr className="bg-gray-50 border-t border-gray-100">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="grid grid-cols-3 gap-4 text-xs">
                          <div>
                            <p className="text-[10px] text-gray-400 mb-1">Notes</p>
                            <p className="text-gray-700">{entry.notes || '(none)'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-400 mb-1">Source</p>
                            <p className="text-gray-700">{entry.source || 'manual'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-400 mb-1">Last Updated</p>
                            <p className="text-gray-700">{entry.updated_at ? new Date(entry.updated_at).toLocaleString() : '—'}</p>
                          </div>
                        </div>
                        {entry.audit_history?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-[10px] text-gray-400 mb-1">History</p>
                            <div className="space-y-1">
                              {entry.audit_history.slice(-5).reverse().map((h, j) => (
                                <div key={j} className="flex items-center gap-2 text-[10px] text-gray-500">
                                  <span className="px-1 py-0 bg-gray-200 rounded">{h.action}</span>
                                  <span>{h.details}</span>
                                  <span className="text-gray-400">by {h.by}</span>
                                  <span className="text-gray-400">{new Date(h.at).toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <EntryModal onSave={fetchEntries} onClose={() => setShowAddModal(false)} />
      )}
      {editEntry && (
        <EntryModal entry={editEntry} onSave={fetchEntries} onClose={() => setEditEntry(null)} />
      )}
      {showAuditLog && <AuditLog onClose={() => setShowAuditLog(false)} />}
    </div>
    </IntelligenceLayout>
  );
}
